import React, { useState, useEffect } from 'react';
import { HeartHandshake, Trash2, Eye, EyeOff, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const STATUSES = { needs_review: 'Needs Review', approved: 'Approved', declined: 'Declined' };
const STATUS_STYLES = {
  needs_review: 'bg-amber-50 text-amber-800 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  declined: 'bg-red-50 text-red-700 border-red-200',
};
const ROLE_LABELS = {
  resident: 'Resident', community_group: 'Community Group', councillor: 'Councillor',
  councillor_candidate: 'Councillor Candidate', mayor: 'Mayor', mayor_candidate: 'Mayor Candidate',
};
const DISPLAY_LABELS = { full_name: 'Full name', first_name: 'First name', pseudonym: 'Pseudonym', anonymous: 'Anonymous' };
const WARD_LABELS = {
  1: 'Ward 1', 2: 'Ward 2', 3: 'Ward 3', 4: 'Ward 4', 5: 'Ward 5', 6: 'Ward 6', 7: 'Ward 7', 8: 'Ward 8',
  citywide: 'Citywide / Mayor', not_provided: 'Ward not provided',
};

export default function AdminPitaSupporters() {
  const [items, setItems] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});
  const load = () => api.get('/admin/pita/supporters').then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const update = async (item, updates, okMsg) => {
    try {
      await api.put(`/admin/pita/supporters/${item.id}`, updates);
      toast.success(okMsg || 'Updated'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Delete supporter record for ${item.name}?`)) return;
    await api.delete(`/admin/pita/supporters/${item.id}`); toast.success('Deleted'); load();
  };

  if (items === null) return <p className="text-sm text-slate-400 py-6">Loading…</p>;

  return (
    <div data-testid="admin-pita-supporters-page">
      <div className="flex items-center gap-3 mb-1">
        <HeartHandshake className="h-6 w-6 text-[#1a2744]" />
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">PITA Supporters</h1>
      </div>
      <p className="text-sm text-slate-500 mb-4">Public support submissions for the Public Issue Tracker App (PITA). Everything starts private, needs review, and unpublished.</p>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-600 leading-relaxed mb-4" data-testid="pita-supporters-admin-note">
        <strong>Publishing rules (locked):</strong> a supporter appears publicly only when they gave consent, you approve
        them, AND you publish them. The public page shows only their chosen display name, role, ward group, and
        statement. Emails and private notes are never public. Verify official roles (Councillor, Mayor, candidates)
        before approving.
      </div>

      {items.length === 0 && <Card><CardContent className="p-8 text-center text-sm text-slate-500" data-testid="pita-supporters-admin-empty">No supporter submissions yet. Submissions from /support-pita appear here.</CardContent></Card>}

      <div className="space-y-4">
        {items.map((s) => {
          const canPublish = s.consent_public && s.status === 'approved';
          return (
            <Card key={s.id} data-testid={`pita-supporter-admin-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-bold text-[#1a2744]">{s.name}</p>
                      <Badge variant="outline" className={`text-[10px] font-bold ${STATUS_STYLES[s.status] || ''}`}>{STATUSES[s.status] || s.status}</Badge>
                      <Badge variant="outline" className={`text-[10px] font-bold ${s.published ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-300'}`}>
                        {s.published ? 'PUBLISHED' : 'NOT PUBLISHED'}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">{ROLE_LABELS[s.role] || s.role}</Badge>
                      <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">{WARD_LABELS[s.ward] || s.ward}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {s.email} (private) · Display: {DISPLAY_LABELS[s.display_choice] || s.display_choice}{s.display_choice === 'pseudonym' && s.pseudonym ? ` (“${s.pseudonym}”)` : ''} ·
                      Consent to public listing: <strong>{s.consent_public ? 'YES' : 'NO'}</strong> · {(s.created_at || '').slice(0, 10)}
                    </p>
                    {s.organization && <p className="text-xs text-slate-500 mt-0.5">Organization: {s.organization}</p>}
                    {s.statement && <p className="text-sm text-slate-600 mt-2 leading-relaxed border-l-2 border-slate-200 pl-3 italic">“{s.statement}”</p>}
                    <div className="mt-3">
                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Private admin notes</label>
                      <Textarea rows={2} value={notesDraft[s.id] ?? s.admin_notes ?? ''} onChange={(e) => setNotesDraft((d) => ({ ...d, [s.id]: e.target.value }))} className="text-sm" data-testid={`pita-supporter-notes-${s.id}`} />
                      <Button size="sm" variant="outline" onClick={() => update(s, { admin_notes: notesDraft[s.id] ?? s.admin_notes ?? '' }, 'Notes saved')} className="h-8 text-xs mt-1.5 font-bold" data-testid={`pita-supporter-save-notes-${s.id}`}>Save Notes</Button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0 w-full lg:w-56">
                    <Select value={s.status} onValueChange={(v) => update(s, { status: v }, `Marked as ${STATUSES[v]}`)}>
                      <SelectTrigger className="h-9 text-xs" data-testid={`pita-supporter-status-${s.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(STATUSES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    {s.published ? (
                      <Button size="sm" variant="outline" onClick={() => update(s, { published: false }, 'Unpublished')} className="h-9 text-xs font-bold" data-testid={`pita-supporter-unpublish-${s.id}`}>
                        <EyeOff className="h-3.5 w-3.5 mr-1" /> Unpublish
                      </Button>
                    ) : (
                      <Button size="sm" disabled={!canPublish} onClick={() => update(s, { published: true }, 'Published to the public supporters page')} className="h-9 text-xs font-bold bg-[#1a2744] hover:bg-[#243456] text-white disabled:opacity-50" data-testid={`pita-supporter-publish-${s.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> Publish
                      </Button>
                    )}
                    {!canPublish && !s.published && (
                      <p className="text-[10px] text-slate-400 leading-snug flex gap-1"><ShieldCheck className="h-3 w-3 shrink-0 mt-0.5" /> Needs consent + Approved status before publishing.</p>
                    )}
                    <Button size="sm" variant="outline" onClick={() => remove(s)} className="h-9 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`pita-supporter-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
